"use client";

import Link from "next/link";
import { FormEvent, useEffect, useState } from "react";
import { Camera, CheckCircle2, FileImage, LoaderCircle, X } from "lucide-react";
import type { Service } from "@/lib/content-types";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { formatPhoneInput } from "@/lib/phone";

const MAX_PHOTO_SIZE = 8 * 1024 * 1024;

export function BookingDrawer({ service, startAt, onClose, onBooked }: { service: Service; startAt: string | null; onClose: () => void; onBooked: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [photoName, setPhotoName] = useState("");
  const [desiredStyle, setDesiredStyle] = useState("");
  const [desiredLength, setDesiredLength] = useState("");
  const [profile, setProfile] = useState<{ id: string; firstName: string; lastName: string | null; email: string; phone: string | null; instagram: string | null } | null>(null);

  useEffect(() => {
    let active = true;
    void fetch("/api/auth/session", { cache: "no-store" }).then((response) => response.json()).then((data) => { if (active) setProfile(data.user || null); }).catch(() => undefined);
    return () => { active = false; };
  }, []);

  if (!startAt) return null;
  const selectedStartAt = startAt;

  function resetState() {
    setSuccess(false);
    setError("");
    setPhotoName("");
    setDesiredStyle("");
    setDesiredLength("");
  }

  function handleClose() {
    resetState();
    onClose();
  }

  function handleBooked() {
    resetState();
    onBooked();
  }

  const displayDate = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date(selectedStartAt.replace(" ", "T")));

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const photo = form.get("inspirationPhoto");

    if (photo instanceof File && photo.size > MAX_PHOTO_SIZE) {
      setError("La photo ne doit pas dépasser 8 Mo.");
      setSubmitting(false);
      return;
    }

    const textValue = (name: string) => {
      const value = form.get(name);
      return typeof value === "string" ? value.trim() : "";
    };
    const desiredStyle = textValue("desiredStyle");
    const desiredLength = textValue("desiredLength");
    const alternativeSlots = textValue("alternativeSlots");
    const customerNotes = textValue("notes");
    const notes = [
      desiredStyle ? `Style souhaité : ${desiredStyle}` : "",
      desiredLength ? `Longueur : ${desiredLength}` : "",
      alternativeSlots ? `Autres disponibilités : ${alternativeSlots}` : "",
      customerNotes ? `Précisions : ${customerNotes}` : "",
    ].filter(Boolean).join("\n");

    form.set("serviceId", service.id);
    form.set("startAt", selectedStartAt);
    form.set("notes", notes);
    form.set("acceptedPrivacy", form.get("acceptedPrivacy") === "on" ? "true" : "false");
    form.set("acceptedBookingTerms", form.get("acceptedBookingTerms") === "on" ? "true" : "false");

    try {
      const response = await fetch("/api/appointments", { method: "POST", body: form });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || data.error || "La demande n’a pas pu être enregistrée.");
      setSuccess(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer open={Boolean(startAt)} onOpenChange={(open) => { if (!open) handleClose(); }} direction="right">
      <DrawerContent className="booking-drawer" aria-describedby="booking-description">
        <DrawerHeader className="booking-drawer-header">
          <DrawerClose asChild><button className="drawer-close" type="button" aria-label="Fermer"><X /></button></DrawerClose>
          <p className="section-label">Demande de rendez-vous</p>
          <DrawerTitle>{service.name}</DrawerTitle>
          <DrawerDescription id="booking-description" className="drawer-date">{displayDate}</DrawerDescription>
        </DrawerHeader>
        <div className="booking-drawer-body">
        {success ? (
          <div className="booking-success"><CheckCircle2 size={46} /><h2>Demande envoyée</h2><p>Le créneau est réservé provisoirement. Le rendez-vous devient définitif après confirmation.</p><Link href={profile ? "/espace-client" : "/mes-demandes"} className="button button-ghost">{profile ? "Voir mon espace" : "Voir mes demandes"}</Link><button className="button button-primary" type="button" onClick={handleBooked}>Terminer</button></div>
        ) : (
          <>
            <form key={profile?.id || "anonymous"} className="booking-form" onSubmit={submit} encType="multipart/form-data">
              {profile ? <div className="booking-account-notice"><strong>Compte connecté</strong><span>La demande sera ajoutée à votre espace client.</span></div> : <div className="booking-account-notice anonymous"><strong>Sans compte</strong><span>La demande restera accessible sur ce navigateur. <Link href="/connexion">Se connecter</Link></span></div>}
              <div className="form-row"><label>Prénom *<Input required name="firstName" autoComplete="given-name" defaultValue={profile?.firstName || ""} readOnly={Boolean(profile)} aria-readonly={Boolean(profile)} /></label><label>Nom<Input name="lastName" autoComplete="family-name" defaultValue={profile?.lastName || ""} readOnly={Boolean(profile)} aria-readonly={Boolean(profile)} /></label></div>
              <label>Téléphone *<Input required name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="06 12 34 56 78" defaultValue={formatPhoneInput(profile?.phone || "")} readOnly={Boolean(profile)} aria-readonly={Boolean(profile)} onBlur={(event) => { event.currentTarget.value = formatPhoneInput(event.currentTarget.value); }} /></label>
              <p className="field-help">Le numéro sert uniquement à organiser le rendez-vous.</p>
              <label>E-mail * <span>(nécessaire pour recevoir la confirmation)</span><Input required name="email" type="email" autoComplete="email" defaultValue={profile?.email || ""} readOnly={Boolean(profile)} aria-readonly={Boolean(profile)} /></label>
              <label>Instagram <span>(optionnel)</span><Input name="instagram" placeholder="@pseudo" defaultValue={profile?.instagram || ""} readOnly={Boolean(profile)} aria-readonly={Boolean(profile)} /></label>
              <div className="form-row">
                <label>Style souhaité<Input type="hidden" name="desiredStyle" value={desiredStyle} /><Select value={desiredStyle} onValueChange={setDesiredStyle}><SelectTrigger><SelectValue placeholder="À préciser" /></SelectTrigger><SelectContent><SelectGroup><SelectItem value="Simple / naturel">Simple / naturel</SelectItem><SelectItem value="Élaboré">Élaboré</SelectItem><SelectItem value="Avec décoration">Avec décoration</SelectItem><SelectItem value="Je souhaite un conseil">Je souhaite un conseil</SelectItem></SelectGroup></SelectContent></Select></label>
                <label>Longueur<Input type="hidden" name="desiredLength" value={desiredLength} /><Select value={desiredLength} onValueChange={setDesiredLength}><SelectTrigger><SelectValue placeholder="À préciser" /></SelectTrigger><SelectContent><SelectGroup><SelectItem value="Courte">Courte</SelectItem><SelectItem value="Moyenne">Moyenne</SelectItem><SelectItem value="Longue">Longue</SelectItem><SelectItem value="Très longue">Très longue</SelectItem></SelectGroup></SelectContent></Select></label>
              </div>
              <label>Autres disponibilités <span>(optionnel)</span><Input name="alternativeSlots" placeholder="Ex. mardi après 15 h ou samedi matin" /></label>
              <label>Modèle ou précisions<Textarea name="notes" rows={4} placeholder="Couleurs, type de tresses, décorations, contraintes particulières…" /></label>
              <label className="photo-upload-label">
                <span><Camera size={18} /> Photo d’inspiration <small>(optionnelle)</small></span>
                <input name="inspirationPhoto" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => {
                  const file = event.target.files?.[0];
                  setPhotoName(file?.name || "");
                  if (file && file.size > MAX_PHOTO_SIZE) setError("La photo ne doit pas dépasser 8 Mo.");
                  else setError("");
                }} />
                <i>{photoName ? <><FileImage size={17} /> {photoName}</> : "JPG, PNG ou WebP — 8 Mo maximum"}</i>
              </label>
              <label className="honeypot" aria-hidden="true">Site web<input name="website" tabIndex={-1} autoComplete="off" /></label>
              <div className="form-legal-notice">
                Les données marquées d’un astérisque et la photo éventuelle servent uniquement à traiter la demande. Consultez la <Link href="/confidentialite" target="_blank">politique de confidentialité</Link>.
              </div>
              <label className="checkbox-label"><input required name="acceptedPrivacy" type="checkbox" /> <span>J’ai lu la <Link href="/confidentialite" target="_blank">politique de confidentialité</Link>.</span></label>
              <label className="checkbox-label"><input required name="acceptedBookingTerms" type="checkbox" /> <span>J’accepte les <Link href="/conditions-reservation" target="_blank">conditions de réservation</Link>.</span></label>
              {error && <p className="form-error">{error}</p>}
              <button className="button button-primary button-full" disabled={submitting} type="submit">{submitting ? <><LoaderCircle className="spin" size={18} /> Envoi…</> : "Envoyer la demande"}</button>
            </form>
          </>
        )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
